import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Building2, Users, Layers, Edit2, Save, X, 
  Loader2, CheckCircle, AlertTriangle, Infinity, RefreshCw, History
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCompanyLimitsPage = () => {
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem('admin_token'));
  
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingCompany, setEditingCompany] = useState(null);
  const [editForm, setEditForm] = useState({ max_rounds: '', max_participants: '' });
  const [saving, setSaving] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyLimits, setCompanyLimits] = useState(null);
  const [loadingLimits, setLoadingLimits] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchCompanies();
  }, [token, navigate]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(res.data.companies || res.data || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyLimits = async (companyId) => {
    try {
      setLoadingLimits(true);
      const res = await axios.get(`${API}/api/admin/companies/${companyId}/limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyLimits(res.data);
      setSelectedCompany(companyId);
    } catch (err) {
      console.error('Error fetching limits:', err);
      toast.error('Failed to load company limits');
    } finally {
      setLoadingLimits(false);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company.id);
    setEditForm({
      max_rounds: company.custom_limits?.max_rounds ?? '',
      max_participants: company.custom_limits?.max_participants ?? ''
    });
  };

  const handleSave = async (companyId) => {
    try {
      setSaving(true);
      
      const limits = {};
      if (editForm.max_rounds !== '') {
        limits.max_rounds = editForm.max_rounds === '-1' ? -1 : parseInt(editForm.max_rounds) || null;
      } else {
        limits.max_rounds = null; // Remove override
      }
      if (editForm.max_participants !== '') {
        limits.max_participants = editForm.max_participants === '-1' ? -1 : parseInt(editForm.max_participants) || null;
      } else {
        limits.max_participants = null;
      }
      
      await axios.put(
        `${API}/api/admin/companies/${companyId}/custom-limits`,
        limits,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Limits updated successfully');
      setEditingCompany(null);
      fetchCompanies();
      
      if (selectedCompany === companyId) {
        fetchCompanyLimits(companyId);
      }
    } catch (err) {
      console.error('Error saving limits:', err);
      toast.error(err.response?.data?.detail || 'Failed to save limits');
    } finally {
      setSaving(false);
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatLimit = (value) => {
    if (value === -1) return <span className="text-purple-600 font-semibold flex items-center gap-1"><Infinity size={14} /> Unlimited</span>;
    if (value === null || value === undefined) return <span className="text-gray-400">Default</span>;
    return <span className="font-semibold">{value}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Company Limits Management</h1>
                <p className="text-sm text-gray-500">Override plan limits for companies</p>
              </div>
            </div>
            <button
              onClick={fetchCompanies}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={20} className="text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Companies List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border shadow-sm">
              {/* Search */}
              <div className="p-4 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search companies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none transition-all"
                  />
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Company</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Max Rounds</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Max Participants</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredCompanies.map(company => (
                      <tr 
                        key={company.id} 
                        className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedCompany === company.id ? 'bg-purple-50' : ''}`}
                        onClick={() => fetchCompanyLimits(company.id)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold">
                              {company.name?.charAt(0) || 'C'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{company.name}</p>
                              <p className="text-xs text-gray-500">{company.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {editingCompany === company.id ? (
                            <input
                              type="text"
                              value={editForm.max_rounds}
                              onChange={(e) => setEditForm(prev => ({ ...prev, max_rounds: e.target.value }))}
                              className="w-20 px-2 py-1 border rounded text-center text-sm"
                              placeholder="-1 = ∞"
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            formatLimit(company.custom_limits?.max_rounds)
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {editingCompany === company.id ? (
                            <input
                              type="text"
                              value={editForm.max_participants}
                              onChange={(e) => setEditForm(prev => ({ ...prev, max_participants: e.target.value }))}
                              className="w-24 px-2 py-1 border rounded text-center text-sm"
                              placeholder="-1 = ∞"
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            formatLimit(company.custom_limits?.max_participants)
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            {editingCompany === company.id ? (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSave(company.id); }}
                                  disabled={saving}
                                  className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                                >
                                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingCompany(null); }}
                                  className="p-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                  <X size={16} />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleEdit(company); }}
                                className="p-1.5 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors"
                              >
                                <Edit2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredCompanies.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No companies found</p>
                </div>
              )}
            </div>
          </div>

          {/* Company Details Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border shadow-sm sticky top-24">
              {loadingLimits ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-6 h-6 text-purple-600 animate-spin mx-auto" />
                </div>
              ) : companyLimits ? (
                <>
                  <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-indigo-50">
                    <h3 className="font-semibold text-gray-900">{companyLimits.company_name}</h3>
                    <p className="text-xs text-gray-500 mt-1">ID: {companyLimits.company_id}</p>
                  </div>
                  
                  <div className="p-4 space-y-4">
                    {/* Subscription Info */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Subscription</h4>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Plan</span>
                          <span className="font-semibold text-purple-600 capitalize">
                            {companyLimits.subscription?.plan_name || 'Basic'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-sm text-gray-600">Status</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            companyLimits.subscription?.status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {companyLimits.subscription?.status || 'None'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Plan Limits */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Plan Limits</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <Layers className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                          <p className="text-xs text-blue-600">Rounds</p>
                          <p className="font-bold text-blue-800">
                            {companyLimits.plan_limits?.max_rounds === -1 ? '∞' : companyLimits.plan_limits?.max_rounds || 1}
                          </p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <Users className="w-5 h-5 text-green-600 mx-auto mb-1" />
                          <p className="text-xs text-green-600">Participants</p>
                          <p className="font-bold text-green-800">
                            {companyLimits.plan_limits?.max_participants === -1 ? '∞' : (companyLimits.plan_limits?.max_participants || 1000).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Custom Overrides */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Custom Overrides</h4>
                      {Object.keys(companyLimits.custom_limits || {}).length > 0 ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <span className="text-xs font-medium text-amber-700">Custom limits active</span>
                          </div>
                          <div className="space-y-1 text-sm">
                            {companyLimits.custom_limits?.max_rounds !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-amber-700">Max Rounds:</span>
                                <span className="font-semibold text-amber-800">
                                  {companyLimits.custom_limits.max_rounds === -1 ? '∞ Unlimited' : companyLimits.custom_limits.max_rounds}
                                </span>
                              </div>
                            )}
                            {companyLimits.custom_limits?.max_participants !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-amber-700">Max Participants:</span>
                                <span className="font-semibold text-amber-800">
                                  {companyLimits.custom_limits.max_participants === -1 ? '∞ Unlimited' : companyLimits.custom_limits.max_participants?.toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-3 text-center text-sm text-gray-500">
                          No custom overrides
                        </div>
                      )}
                    </div>
                    
                    {/* Effective Limits */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Effective Limits</h4>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-4 h-4 text-purple-600" />
                          <span className="text-xs font-medium text-purple-700">Currently applied</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-center">
                          <div>
                            <p className="text-xs text-purple-600">Rounds</p>
                            <p className="font-bold text-purple-800 text-lg">
                              {companyLimits.effective_limits?.max_rounds === -1 ? '∞' : companyLimits.effective_limits?.max_rounds || 1}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-purple-600">Participants</p>
                            <p className="font-bold text-purple-800 text-lg">
                              {companyLimits.effective_limits?.max_participants === -1 ? '∞' : (companyLimits.effective_limits?.max_participants || 1000).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">Select a company to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCompanyLimitsPage;
